// Market catalog — data and display helpers for the customer marketplace.
//
// Every read goes through market_products_public / market_product_skus_public
// and the catalog RPCs (supabase/migrations/20261020014736_market_catalog.sql). Those return rows
// only while market_settings.marketplace_public is on — or to an admin, who can
// rehearse. Opening the market is that one setting; nothing here decides it.
//
// Brand rule (non-negotiable): nothing on these pages may name or link the
// source marketplaces or the provider. The views carry only the customer-safe
// shape; images come from MAABAR's own market-image function, never from the
// source CDN.

import { SUPABASE_FUNCTIONS_URL } from '../supabase';
import { marketDb, marketRpc } from './marketDb';
import { dedupeCollections } from './marketCollections';
import { isUkPlug } from './variantSplit';

const PRODUCT_COLUMNS = [
  'id', 'title_ar', 'title_en', 'title_zh', 'description_ar', 'description_en',
  'specs', 'options', 'images', 'category', 'subcategory', 'product_type', 'seller_name_latin', 'seller_rating',
  'price_cny', 'price_min_cny', 'price_max_cny', 'price_tiers', 'moq', 'order_step', 'sale_unit',
  'stock_total', 'sku_count', 'skus_in_stock', 'featured_rank', 'created_at',
].join(', ');
const SKU_COLUMNS = 'key, selection, price_cny, stock, moq, image_ref, available';

// The public view lists only active, not-hidden products: a row it returns is
// for sale. `status` is set here so the cart's checks read the same as before.
const active = (p) => (p ? { ...p, status: 'active' } : null);

// thumb → a 400×400 copy for cards and option chips; the gallery keeps full size.
export function marketImageUrl(ref, thumb = false) {
  return ref ? `${SUPABASE_FUNCTIONS_URL}/market-image/${ref}${thumb ? '?s=t' : ''}` : null;
}

const filterArgs = (opts) => ({
  p_q: opts.q || null, p_category: opts.category || null, p_subcategory: opts.subcategory || null, p_product_type: opts.productType || null,
  p_price_min_sar: opts.priceMin ?? null, p_price_max_sar: opts.priceMax ?? null, p_moq_max: opts.moqMax ?? null,
  p_min_rating: opts.minRating ?? null, p_has_tiers: opts.hasTiers ?? null, p_has_variants: opts.hasVariants ?? null,
  p_attrs: opts.attrs && Object.keys(opts.attrs).length ? opts.attrs : null, p_seller: opts.seller || null,
});

/**
 * Listing, category pages and search — one RPC. Filters are on real data only.
 * opts: { q, category, subcategory, productType, priceMin, priceMax, moqMax, minRating, hasTiers, hasVariants,
 *         attrs: { material: ['plastic'], … } (structured attribute KEYS), seller, sort, limit, offset }
 * → { total, rows, error }
 */
export async function searchMarket(opts = {}) {
  const { data, error } = await marketDb.rpc('market_search', { ...filterArgs(opts), p_sort: opts.sort || 'relevance', p_limit: opts.limit ?? 24, p_offset: opts.offset ?? 0 });
  return { total: data?.total ?? 0, rows: (data?.rows ?? []).map(active), error };
}

/** Attribute facets for the same filters: [{ attr, value, count }] (disjunctive counts — see market_facets). */
export async function fetchFacets(opts = {}) {
  const { data, error } = await marketDb.rpc('market_facets', filterArgs(opts));
  return { facets: Array.isArray(data) ? data : [], error };
}

export async function suggestMarket(q, lang) {
  if (!q || q.trim().length < 2) return [];
  const { data } = await marketDb.rpc('market_search_suggest', { p_q: q, p_lang: lang, p_limit: 8 });
  return Array.isArray(data) ? data : [];
}

/** {category: {total, leaves: {subcategory: {total, types: {product_type: n}}}}} — only what is listed. */
export async function fetchCategoryCounts() {
  const { data, error } = await marketDb.rpc('market_category_counts');
  const out = {};
  for (const r of data ?? []) {
    out[r.category] ??= { total: 0, leaves: {} };
    out[r.category].total += r.count;
    if (!r.subcategory) continue;
    const l = (out[r.category].leaves[r.subcategory] ??= { total: 0, types: {} });
    l.total += r.count;
    if (r.product_type) l.types[r.product_type] = r.count;
  }
  return { counts: out, error };
}

// Category showcase: counts + real product images per main category,
// subcategory and product type —
//   {category: {total, images, leaves: {subcategory: {count, images, types: {product_type: {count, images}}}}}}
// Only what is listed: an empty category is simply absent, so pages never show
// it. One request per page load, shared by the menu, home and category pages.
let showcase = null;
export function fetchShowcase({ refresh = false } = {}) {
  if (!showcase || refresh) {
    showcase = marketDb.rpc('market_category_showcase').then(({ data }) => {
      const out = {};
      for (const r of Array.isArray(data) ? data : []) {
        out[r.category] ??= { total: 0, images: [], leaves: {} };
        if (r.product_type) {
          const l = (out[r.category].leaves[r.subcategory] ??= { count: 0, images: [], types: {} });
          l.types[r.product_type] = { count: r.count, images: r.images || [] };
        } else if (r.subcategory) {
          const l = (out[r.category].leaves[r.subcategory] ??= { count: 0, images: [], types: {} });
          l.count = r.count; l.images = r.images || [];
        } else { out[r.category].total = r.count; out[r.category].images = r.images || []; }
      }
      return out;
    }, () => ({}));
  }
  return showcase;
}

/** The home page's curated collections, in page order, each product once (lib/marketCollections.js). */
export async function fetchHomeCollections() {
  const { data, error } = await marketDb.rpc('market_home_collections');
  return { collections: dedupeCollections(data).map((c) => ({ ...c, rows: c.rows.map(active) })), error };
}

/** One collection in full (its "see all" page). → { collection | null, error } */
export async function fetchCollection(slug, limit = 24, offset = 0) {
  const { data, error } = await marketDb.rpc('market_collection', { p_slug: slug, p_limit: limit, p_offset: offset });
  return { collection: data ? { ...data, rows: (data.rows || []).map(active) } : null, error };
}

/** Suppliers in the market: [{ name, products, service_score, repeat_purchase_rate, categories, samples:[{id,image,title_*}] }]. */
export async function fetchTopSellers(category = null, limit = 8) {
  const { data } = await marketDb.rpc('market_top_sellers', { p_category: category, p_limit: limit });
  return Array.isArray(data) ? data : [];
}

export async function fetchRelated(id) {
  const [r, s] = await Promise.all([
    marketDb.rpc('market_related', { p_product_id: id, p_limit: 8 }),
    marketDb.rpc('market_same_seller', { p_product_id: id, p_limit: 8 }),
  ]);
  return { related: (r.data ?? []).map(active), sameSeller: (s.data ?? []).map(active) };
}

/** Products by id, for favorites, comparison and recently viewed. Unlisted ones are simply absent. */
export async function fetchMarketCards(ids) {
  const list = [...new Set(ids)].filter(Boolean);
  if (!list.length) return { rows: [], error: null };
  const { data, error } = await marketDb.from('market_products_public').select(PRODUCT_COLUMNS).in('id', list);
  const byId = Object.fromEntries((data ?? []).map((p) => [p.id, active(p)]));
  return { rows: list.map((id) => byId[id]).filter(Boolean), error };
}

export async function fetchMarketProduct(id) {
  const [{ data: p, error: e1 }, { data: skus, error: e2 }] = await Promise.all([
    marketDb.from('market_products_public').select(PRODUCT_COLUMNS).eq('id', id).maybeSingle(),
    marketDb.from('market_product_skus_public').select(SKU_COLUMNS).eq('product_id', id),
  ]);
  return { product: active(p), skus: skus || [], error: e1 || e2 };
}

/**
 * Fresh rows for a cart — a product no longer listed is absent, which the cart
 * reports as gone rather than silently dropping. { products: {id: row}, skus: {id: rows} }.
 */
export async function fetchMarketProductsByIds(ids) {
  const list = [...new Set(ids)].filter(Boolean);
  if (!list.length) return { products: {}, skus: {}, error: null };
  // One SKU request per product: the API caps a response at 1,000 rows and a
  // single product has up to 489 SKUs, so one combined request would truncate.
  const [{ data: ps, error: e1 }, ...skuRes] = await Promise.all([
    marketDb.from('market_products_public').select(PRODUCT_COLUMNS).in('id', list),
    ...list.map((id) => marketDb.from('market_product_skus_public').select(SKU_COLUMNS).eq('product_id', id)),
  ]);
  const products = Object.fromEntries((ps || []).map((p) => [p.id, active(p)]));
  const skus = Object.fromEntries(list.map((id, i) => [id, skuRes[i].data || []]));
  return { products, skus, error: e1 || skuRes.find((r) => r.error)?.error || null };
}

// ── favorites (signed-in, persistent) ───────────────────────────────────────
export async function fetchFavoriteIds() {
  const { data, error } = await marketDb.from('market_favorites').select('product_id, created_at').order('created_at', { ascending: false });
  return { ids: (data ?? []).map((r) => r.product_id), error };
}
export const toggleFavorite = (productId) => marketRpc('market_favorite_toggle', { p_product_id: productId });

// Emoji and pictographs in supplier text (❤, ★ stickers, ✅ …) are decoration,
// not product information: the market shows none (owner's no-emoji rule). The
// stored text is untouched; only what is displayed is cleaned.
export const noPictographs = (t) => String(t ?? '').replace(/[\p{Extended_Pictographic}\u2600-\u27BF\u2B50\u2B55\uFE0F\u200D]/gu, ' ').replace(/\s{2,}/g, ' ').trim();

export function marketTitle(p, lang) {
  if (!p) return '';
  if (lang === 'ar') return noPictographs(p.title_ar || p.title_en || '');
  if (lang === 'zh') return noPictographs(p.title_zh || p.title_en || p.title_ar || '');
  return noPictographs(p.title_en || p.title_ar || '');
}

/** Option label in the page language, falling back through the others. */
export function optionLabel(v, lang) {
  if (!v) return '';
  if (lang === 'ar') return noPictographs(v.label_ar || v.label_en || v.label_zh || '');
  if (lang === 'zh') return noPictographs(v.label_zh || v.label_en || '');
  return noPictographs(v.label_en || v.label_ar || '');
}

export function axisName(a, lang) {
  if (!a) return '';
  if (lang === 'ar') return a.name_ar || a.name_en || '';
  if (lang === 'zh') return a.name_zh || a.name_en || '';
  return a.name_en || a.name_ar || '';
}

export function specText(s, lang) {
  if (lang === 'ar') return { name: s.name_ar || s.name_en, value: s.value_ar || s.value_en };
  if (lang === 'zh') return { name: s.name_zh || s.name_en, value: s.value_zh || s.value_en };
  return { name: s.name_en || s.name_ar, value: s.value_en || s.value_ar };
}

// Prices: see marketPricing.js — the one place CNY becomes SAR for these pages.

/**
 * Variant selection over up to N axes. `skus[i].selection[a]` is the value
 * index on axis a (-1 = the SKU has no value on that axis). Returns, for each
 * axis/value, whether some AVAILABLE SKU matches it together with the other
 * axes' current picks — the standard "grey out impossible combinations".
 */
export function optionAvailability(axes, skus, picked) {
  return axes.map((ax, a) => ax.values.map((_, v) => skus.some((s) =>
    s.available && (s.stock ?? 0) > 0 && Array.isArray(s.selection) && s.selection[a] === v
    && picked.every((p, b) => b === a || p == null || s.selection[b] === p))));
}

export function skuFor(skus, picked) {
  if (picked.some((p) => p == null)) return null;
  return skus.find((s) => Array.isArray(s.selection) && picked.every((p, a) => s.selection[a] === p)) || null;
}

/** "Red · XL" — a SKU's chosen option labels in the page language. */
export function variantText(product, sku, lang) {
  const axes = product?.options?.axes || [];
  if (!sku || !Array.isArray(sku.selection)) return '';
  return axes.map((ax, a) => (sku.selection[a] >= 0 ? optionLabel(ax.values?.[sku.selection[a]], lang) : '')).filter(Boolean).join(' · ');
}

// The SKU a product opens on, and the one its card is priced at (owner,
// 2026-11-02): the first SKU that is available, in stock and fully chosen,
// ordered by its variant position, then its key. The SAME order as
// market_delivered().default_sku (Postgres int[] order: element by element,
// a shorter prefix first) — change both together.
export function compareSelection(a, b) {
  const x = Array.isArray(a.selection) ? a.selection : []; const y = Array.isArray(b.selection) ? b.selection : [];
  for (let i = 0; i < Math.min(x.length, y.length); i++) if (x[i] !== y[i]) return x[i] - y[i];
  if (x.length !== y.length) return x.length - y.length;
  return String(a.key) < String(b.key) ? -1 : String(a.key) > String(b.key) ? 1 : 0;
}
// A British-plug SKU comes first (owner, 2026-11-02: Saudi Arabia uses it) —
// the same order as the SQL. axes: product.options.axes.
export function skuIsUkPlug(sku, axes) {
  return Array.isArray(sku?.selection) && sku.selection.some((j, a) => j >= 0 && isUkPlug(axes?.[a]?.values?.[j]));
}
export function defaultSku(skus, axes = []) {
  return [...(skus || [])].sort((a, b) => (skuIsUkPlug(b, axes) - skuIsUkPlug(a, axes)) || compareSelection(a, b))
    .find((s) => s.available && (s.stock ?? 0) > 0 && (!Array.isArray(s.selection) || s.selection.every((v) => v >= 0))) || null;
}

/** The stored variant split (market_variant_attrs), or null — the page then splits by the same rules itself. */
export async function fetchVariantAttrs(productId) {
  const { data, error } = await marketDb.from('market_variant_attrs').select('mode, axes, source, parser_version').eq('product_id', productId).maybeSingle();
  if (error) return null;   // the table may not exist yet: the rules run in the browser
  return data || null;
}

/** A product's short name for the page title: up to the first comma or dash, at most 60 characters. */
export function marketShortTitle(p, lang) {
  const t = marketTitle(p, lang) || '';
  const cut = t.split(/[،,|]| - | – /)[0].trim() || t;
  return cut.length > 60 ? `${cut.slice(0, 57).trim()}…` : cut;
}
